import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

import { processLink } from '@/_utils/link.utils'

interface IUploadImage {
	name: string
	fileName: string
	collectionName: string
	size: number
	modelId: number
	modelType: string
	uuid: string
	id: number
	url: string
	originalFile: {
		id: string
		name: string
		path: string
		size: number
		modifiedAt: string
		createdAt: string
		updatedAt: string
		deletedAt: string | null
	}
	customProperties: any
	/**
	 * Deprecated, use customAspectRatio instead
	 */
	aspectRatioUsed?: number

	customAspectRatio?: number
}

export interface ImageData {
	image: IUploadImage
	caption?: string
	displayMode: 'standard' | 'fluid'
	clickableUrl?: string
	alt?: string

	/**
	 * Deprecated, use image.customAspectRatio instead
	 */
	customAspectRatio?: number
}

export interface BEditorJsImageProps {
	data: ImageData
	onClick?: () => void
}

export const BEditorJsImage: React.FC<BEditorJsImageProps> = ({ data, onClick }) => {
	const { caption, displayMode, image, clickableUrl, alt, customAspectRatio } = data

	const backwardCompatibleAspectRation =
		customAspectRatio ?? image?.customAspectRatio ?? image?.aspectRatioUsed

	const widthClass = 'editorjs-renderer_block'

	const imageClasses = {
		standard: `aspect-[658/403] rounded-[21px] overflow-hidden`,
		fluid: 'h-[687px]'
	}

	let height = displayMode === 'fluid' ? 687 : 403
	let width = displayMode === 'fluid' ? 1920 : 658
	let aspectRatioClass = imageClasses[displayMode] || imageClasses.standard

	// For standard mode: use custom aspect ratio if available, otherwise default
	const hasCustomAspectRatio = displayMode === 'standard' && backwardCompatibleAspectRation

	if (image?.customProperties?.editorJsHeight && image?.customProperties?.editorJsWidth) {
		height = image.customProperties.editorJsHeight
		width = image.customProperties.editorJsWidth
		aspectRatioClass = ''
	}

	// When custom aspect ratio is provided, clear the aspect ratio class and use inline style
	if (hasCustomAspectRatio) {
		aspectRatioClass = aspectRatioClass.replace('aspect-[658/403]', '').trim()
	}

	const containerStyle = hasCustomAspectRatio
		? { aspectRatio: backwardCompatibleAspectRation.toString() }
		: undefined

	return (
		<figure className={`my-14`}>
			<div className={`${displayMode === 'fluid' ? 'w-full' : widthClass}`}>
				<div
					className={clsx(
						aspectRatioClass,
						(clickableUrl && clickableUrl?.length) || onClick ? 'cursor-pointer' : '',
						'bg-[#DED3B9]'
					)}
					style={containerStyle}
				>
					{clickableUrl && clickableUrl?.length ? (
						<Link {...processLink(clickableUrl)}>
							<Image
								unoptimized={true}
								src={
									image?.url ||
									`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/placeholder-image.webp`
								}
								alt={alt || caption || 'Image'}
								className={`w-full ${aspectRatioClass} object-cover h-full`}
								loading='lazy'
								height={height}
								width={width}
							/>
						</Link>
					) : (
						<Image
							unoptimized={true}
							src={
								image?.url ||
								`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/placeholder-image.webp`
							}
							alt={alt || caption || 'Image'}
							className={`w-full ${aspectRatioClass} object-cover h-full`}
							loading='lazy'
							height={height}
							width={width}
						/>
					)}
				</div>
			</div>
			{caption && caption.length > 0 && (
				<figcaption className={`${widthClass} mt-4 text-sm text-[#4A337A] font-light`}>
					{caption}
				</figcaption>
			)}
		</figure>
	)
}

export default BEditorJsImage
